import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useRef } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';

export default function BoostEntries() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [entries, setEntries] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  useEffect(() => { if (!companyId) return; fetch(`${API}/companies/${companyId}/academy/cohorts?per_page=1000`,{headers:getAuthHeaders()}).then(r=>r.json()).then(d=>setCohorts(d.data || [])).catch(()=>setCohorts([])); },[companyId]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');

  const [extraFilters, setExtraFilters] = useState({ cohort_id: '', receipt_id: '', date_from: '', date_to: '', amount_min: '', amount_max: '' });
  const [summary, setSummary] = useState({ count: 0, paid_amount: 0 });
  const fetchSequence = useRef(0);
  const updateFilter = (key, value) => { setExtraFilters(current => ({ ...current, [key]: value })); setPage(1); };

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [form, setForm] = useState({ advertising_receipt_id: '', cohort_id: '', platform_id: '', paid_amount: '', notes: '' });

  // Payment modal
  const [showPayModal, setShowPayModal] = useState(false);
  const [payEntry, setPayEntry] = useState(null);
  const [payForm, setPayForm] = useState({ paid_amount: '', payment_date: '' });

  // Detail modal
  const [showDetail, setShowDetail] = useState(false);
  const [detailEntry, setDetailEntry] = useState(null);

  // Status modal
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusEntry, setStatusEntry] = useState(null);
  const [statusForm, setStatusForm] = useState({ status: '', reason: '' });

  const fetchEntries = () => {
    if (!companyId) return;
    const sequence = ++fetchSequence.current;
    setLoading(true);
    const params = new URLSearchParams({ page, per_page: 15 });
    Object.entries(extraFilters).forEach(([key, value]) => { if (value !== '') params.append(key, value); });
    if (search) params.append('search', search);
    if (statusFilter) params.append('status', statusFilter);
    if (paymentFilter) params.append('payment_status', paymentFilter);
    if (platformFilter) params.append('platform_id', platformFilter);

    fetch(`${API}/companies/${companyId}/boosts/entries?${params}`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => {
        if (sequence !== fetchSequence.current) return;
        setSummary(d.summary || { count: 0, paid_amount: 0 });
        setEntries(Array.isArray(d) ? d : (d && Array.isArray(d.data) ? d.data : []));
        if (d && d.last_page) setLastPage(d.last_page);
        if (d && d.current_page) setPage(d.current_page);
      })
      .catch(() => { if (sequence === fetchSequence.current) { setEntries([]); setSummary(null); } })
      .finally(() => { if (sequence === fetchSequence.current) setLoading(false); });
  };

  const fetchPlatforms = () => {
    if (!companyId) return;
    fetch(`${API}/companies/${companyId}/boosts/platforms`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => setPlatforms(Array.isArray(d) ? d : []))
      .catch(() => setPlatforms([]));
  };

  useEffect(() => { fetchPlatforms(); }, [companyId]);
  useEffect(() => { fetchEntries(); }, [companyId, page, search, statusFilter, paymentFilter, platformFilter, extraFilters]);

  // Create / Edit
  const openCreate = () => {
    setEditEntry(null);
    setForm({ advertising_receipt_id: '', cohort_id: '', platform_id: '', paid_amount: '', notes: '' });
    setShowModal(true);
  };

  const openEdit = (entry) => {
    setEditEntry(entry);
    setForm({
      advertising_receipt_id: entry.advertising_receipt_id || '',
      cohort_id: entry.cohort_id || '',
      platform_id: entry.platform_id || '',
      paid_amount: entry.paid_amount || '',
      notes: entry.notes || '',
    });
    setShowModal(true);
  };

  const saveEntry = () => {
    const url = editEntry
      ? `${API}/companies/${companyId}/boosts/entries/${editEntry.id}`
      : `${API}/companies/${companyId}/boosts/entries`;
    const method = editEntry ? 'PUT' : 'POST';

    fetch(url, {
      method,
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({...form,cohort_id:form.cohort_id || null}),
    })
      .then(r => { if (!r.ok) return r.json().then(e => { throw new Error(e.message || 'Error'); }); return r.json(); })
      .then(() => { setShowModal(false); fetchEntries(); })
      .catch(e => alert(e.message));
  };

  // Delete
  const deleteEntry = (entry) => {
    if (!window.confirm(`Delete boost "${entry.name}"?`)) return;
    fetch(`${API}/companies/${companyId}/boosts/entries/${entry.id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    })
      .then(r => { if (!r.ok) return r.json().then(e => { throw new Error(e.message || 'Error'); }); return r.json(); })
      .then(() => fetchEntries())
      .catch(e => alert(e.message));
  };

  // Archive
  const archiveEntry = (entry) => {
    if (!window.confirm(`Archive boost "${entry.name}"?`)) return;
    fetch(`${API}/companies/${companyId}/boosts/entries/${entry.id}/archive`, {
      method: 'POST',
      headers: getAuthHeaders(),
    })
      .then(r => { if (!r.ok) return r.json().then(e => { throw new Error(e.message || 'Error'); }); return r.json(); })
      .then(() => fetchEntries())
      .catch(e => alert(e.message));
  };

  // Payment
  const openPayment = (entry) => {
    setPayEntry(entry);
    setPayForm({ paid_amount: entry.paid_amount || '', payment_date: new Date().toISOString().split('T')[0] });
    setShowPayModal(true);
  };

  const savePayment = () => {
    fetch(`${API}/companies/${companyId}/boosts/entries/${payEntry.id}/payment`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payForm),
    })
      .then(r => { if (!r.ok) return r.json().then(e => { throw new Error(e.message || 'Error'); }); return r.json(); })
      .then(() => { setShowPayModal(false); fetchEntries(); })
      .catch(e => alert(e.message));
  };

  // Status change
  const openStatusChange = (entry) => {
    setStatusEntry(entry);
    setStatusForm({ status: entry.status, reason: '' });
    setShowStatusModal(true);
  };

  const saveStatus = () => {
    fetch(`${API}/companies/${companyId}/boosts/entries/${statusEntry.id}/status`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(statusForm),
    })
      .then(r => { if (!r.ok) return r.json().then(e => { throw new Error(e.message || 'Error'); }); return r.json(); })
      .then(() => { setShowStatusModal(false); fetchEntries(); })
      .catch(e => alert(e.message));
  };

  // Detail
  const openDetail = (entry) => {
    fetch(`${API}/companies/${companyId}/boosts/entries/${entry.id}`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { setDetailEntry(d.entry || d); setShowDetail(true); })
      .catch(() => alert('Failed to load boost details'));
  };

  if (!companyId) return <div style={pageStyle}><p>Please select a company first.</p></div>;

  return (
    <div style={pageStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: '1.5rem', color: '#1e293b' }}>🚀 Boost Entries</h1>
        <button onClick={openCreate} style={primaryBtnStyle}>+ New Boost</button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
        <input
          placeholder="Search..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          style={{ ...inputStyle, minWidth: 180 }}
        />
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">All Statuses</option>
          <option value="planned">Planned</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select value={paymentFilter} onChange={e => { setPaymentFilter(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">All Payments</option>
          <option value="unpaid">Unpaid</option>
          <option value="partially_paid">Partially Paid</option>
          <option value="paid">Paid</option>
        </select>
        <select value={platformFilter} onChange={e => { setPlatformFilter(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">All Platforms</option>
          {platforms.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(155px, 1fr))', gap: 12, padding: 16, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 18 }}>
        <label style={labelStyle}>Cohort
          <select style={inputStyle} value={extraFilters.cohort_id} onChange={e => updateFilter('cohort_id', e.target.value)}>
            <option value="">All cohorts</option><option value="none">No cohort</option>
            {cohorts.map(c => <option key={c.id} value={c.id}>{c.cohort_code} — {c.name}</option>)}
          </select>
        </label>
        <label style={labelStyle}>Receipt ID<input style={inputStyle} placeholder="Search receipt ID" value={extraFilters.receipt_id} onChange={e => updateFilter('receipt_id', e.target.value)} /></label>
        {[['date_from', 'Recorded from', 'date'], ['date_to', 'Recorded to', 'date'], ['amount_min', 'Minimum paid', 'number'], ['amount_max', 'Maximum paid', 'number']].map(([key, label, type]) => (
          <label key={key} style={labelStyle}>{label}<input style={inputStyle} type={type} min={type === 'number' ? '0' : undefined} step={type === 'number' ? '0.01' : undefined} value={extraFilters[key]} onChange={e => updateFilter(key, e.target.value)} /></label>
        ))}
        <button style={{ ...selectStyle, alignSelf: 'end', height: 40 }} onClick={() => { setSearch(''); setStatusFilter(''); setPaymentFilter(''); setPlatformFilter(''); setExtraFilters({ cohort_id: '', receipt_id: '', date_from: '', date_to: '', amount_min: '', amount_max: '' }); setPage(1); }}>Clear</button>
      </div>
      <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '18px 22px', marginBottom: 18 }}>
        <div style={{ color: '#475569', fontSize: 13 }}>Total paid · matching boosts</div>
        <div style={{ fontSize: 28, fontWeight: 700, color: '#1e40af', margin: '5px 0' }}>{loading ? '…' : summary ? Number(summary.paid_amount).toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : 'Unavailable'}</div>
        <div style={{ fontSize: 13, color: '#64748b' }}>{loading ? 'Updating…' : `${summary?.count ?? 0} entries across all pages`}</div>
      </div>

      {/* Table */}
      {initialLoading ? <p>Loading...</p> : (
        <>
          <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Cohort</th><th style={thStyle}>Platform</th>
                  <th style={thStyle}>Dates</th>
                  <th style={thStyle}>Total</th>
                  <th style={thStyle}>Paid</th>
                  <th style={thStyle}>Remaining</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Payment</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 ? (
                  <tr><td colSpan={9} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No boost entries found</td></tr>
                ) : entries.map(e => (
                  <tr key={e.id} style={{ cursor: 'pointer' }} onClick={() => openDetail(e)}>
                    <td style={tdStyle}>{e.name}</td>
                    <td style={tdStyle}>{e.cohort?.cohort_code || '—'}</td><td style={tdStyle}>{e.platform_name || (e.platform && e.platform.name) || '—'}</td>
                    <td style={{ ...tdStyle, fontSize: '0.78rem', color: '#64748b' }}>
                      {e.start_date || '—'} → {e.end_date || '—'}
                    </td>
                    <td style={tdStyle}>${Number(e.total_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td style={tdStyle}>${Number(e.paid_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td style={tdStyle}>${Number(e.remaining_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td style={tdStyle}><span style={statusBadge(e.status)}>{e.status}</span></td>
                    <td style={tdStyle}><span style={payBadge(e.payment_status)}>{(e.payment_status || '').replace('_', ' ')}</span></td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }} onClick={ev => ev.stopPropagation()}>
                      <button onClick={() => openEdit(e)} style={smallBtnStyle} title="Edit">✏️</button>
                      <button onClick={() => openPayment(e)} style={smallBtnStyle} title="Update Payment">💰</button>
                      <button onClick={() => openStatusChange(e)} style={smallBtnStyle} title="Change Status">🔄</button>
                      {['completed', 'cancelled'].includes(e.status) && (
                        <button onClick={() => archiveEntry(e)} style={smallBtnStyle} title="Archive">📦</button>
                      )}
                      {e.status === 'planned' && Number(e.paid_amount) === 0 && (
                        <button onClick={() => deleteEntry(e)} style={{ ...smallBtnStyle, color: '#ef4444' }} title="Delete">🗑️</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {lastPage > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} style={pageBtnStyle}>← Prev</button>
              <span style={{ padding: '6px 12px', fontSize: '0.85rem', color: '#475569' }}>Page {page} of {lastPage}</span>
              <button disabled={page >= lastPage} onClick={() => setPage(page + 1)} style={pageBtnStyle}>Next →</button>
            </div>
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h2 style={{ margin: '0 0 18px', fontSize: '1.15rem', color: '#1e293b' }}>{editEntry ? 'Edit Boost' : 'New Boost'}</h2>
            <div style={formGrid}>
              <label style={{gridColumn:'1 / -1',fontSize:14,color:'#475569'}}>Advertising receipt ID (optional)
                <input maxLength={255} value={form.advertising_receipt_id || ''} onChange={e=>setForm({...form,advertising_receipt_id:e.target.value})} placeholder="Receipt ID from the advertising platform" style={{...inputStyle,marginTop:6}} />
              </label>
              <label style={{gridColumn:'1 / -1',fontSize:14,color:'#475569'}}>Cohort
                <select value={form.cohort_id || ''} onChange={e=>setForm({...form,cohort_id:e.target.value})} style={{...inputStyle,display:'block',marginTop:6}}>
                  <option value="">No cohort</option>
                  {cohorts.map(c=><option key={c.id} value={c.id}>{c.cohort_code} — {c.name}</option>)}
                </select>
              </label>
              <label style={labelStyle}>
                Platform
                <select value={form.platform_id} onChange={e => setForm({ ...form, platform_id: e.target.value })} style={{...inputStyle,display:'block',marginTop:6}}>
                  <option value="">— Select —</option>
                  {platforms.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </label>
              {!editEntry && (
                <label style={labelStyle}>
                  Paid Amount
                  <input type="number" step="0.01" min="0" value={form.paid_amount} onChange={e => setForm({ ...form, paid_amount: e.target.value })} style={inputStyle} />
                </label>
              )}
            </div>
            <label style={{ ...labelStyle, marginTop: 10 }}>
              Notes
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} style={{ ...inputStyle, minHeight: 60 }} />
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
              <button onClick={() => setShowModal(false)} style={cancelBtnStyle}>Cancel</button>
              <button onClick={saveEntry} style={primaryBtnStyle}>{editEntry ? 'Update' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPayModal && payEntry && (
        <div style={overlayStyle}>
          <div style={{ ...modalStyle, maxWidth: 420 }}>
            <h2 style={{ margin: '0 0 14px', fontSize: '1.1rem', color: '#1e293b' }}>Update Payment — {payEntry.name}</h2>
            <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '0 0 14px' }}>
              Total: ${Number(payEntry.total_amount || 0).toFixed(2)} | Current Paid: ${Number(payEntry.paid_amount || 0).toFixed(2)}
            </p>
            <label style={labelStyle}>
              New Paid Amount
              <input type="number" step="0.01" min="0" max={payEntry.total_amount} value={payForm.paid_amount}
                onChange={e => setPayForm({ ...payForm, paid_amount: e.target.value })} style={inputStyle} />
            </label>
            <label style={{ ...labelStyle, marginTop: 10 }}>
              Payment Date
              <input type="date" value={payForm.payment_date} onChange={e => setPayForm({ ...payForm, payment_date: e.target.value })} style={inputStyle} />
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
              <button onClick={() => setShowPayModal(false)} style={cancelBtnStyle}>Cancel</button>
              <button onClick={savePayment} style={primaryBtnStyle}>Update Payment</button>
            </div>
          </div>
        </div>
      )}

      {/* Status Change Modal */}
      {showStatusModal && statusEntry && (
        <div style={overlayStyle}>
          <div style={{ ...modalStyle, maxWidth: 420 }}>
            <h2 style={{ margin: '0 0 14px', fontSize: '1.1rem', color: '#1e293b' }}>Change Status — {statusEntry.name}</h2>
            <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '0 0 14px' }}>Current: <span style={statusBadge(statusEntry.status)}>{statusEntry.status}</span></p>
            <label style={labelStyle}>
              New Status
              <select value={statusForm.status} onChange={e => setStatusForm({ ...statusForm, status: e.target.value })} style={selectStyle}>
                <option value="planned">Planned</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </label>
            {statusForm.status === 'cancelled' && (
              <label style={{ ...labelStyle, marginTop: 10 }}>
                Reason
                <textarea value={statusForm.reason} onChange={e => setStatusForm({ ...statusForm, reason: e.target.value })} style={{ ...inputStyle, minHeight: 60 }} />
              </label>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
              <button onClick={() => setShowStatusModal(false)} style={cancelBtnStyle}>Cancel</button>
              <button onClick={saveStatus} style={primaryBtnStyle}>Change Status</button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && detailEntry && (
        <div style={overlayStyle}>
          <div style={{ ...modalStyle, maxWidth: 600 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <h2 style={{ margin: 0, fontSize: '1.15rem', color: '#1e293b' }}>{detailEntry.name}</h2>
              <button onClick={() => setShowDetail(false)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#94a3b8' }}>✕</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 20px', marginBottom: 18 }}>
              <div><span style={detailLabel}>Advertising receipt ID:</span> {detailEntry.advertising_receipt_id || '—'}</div>
              <div><span style={detailLabel}>Platform:</span> {detailEntry.platform_name || (detailEntry.platform && detailEntry.platform.name) || '—'}</div>
              <div><span style={detailLabel}>Status:</span> <span style={statusBadge(detailEntry.status)}>{detailEntry.status}</span></div>
              <div><span style={detailLabel}>Start:</span> {detailEntry.start_date || '—'}</div>
              <div><span style={detailLabel}>End:</span> {detailEntry.end_date || '—'}</div>
              <div><span style={detailLabel}>Total:</span> ${Number(detailEntry.total_amount || 0).toFixed(2)}</div>
              <div><span style={detailLabel}>Paid:</span> ${Number(detailEntry.paid_amount || 0).toFixed(2)}</div>
              <div><span style={detailLabel}>Remaining:</span> ${Number(detailEntry.remaining_amount || 0).toFixed(2)}</div>
              <div><span style={detailLabel}>Payment:</span> <span style={payBadge(detailEntry.payment_status)}>{(detailEntry.payment_status || '').replace('_', ' ')}</span></div>
              {detailEntry.created_by_user && <div><span style={detailLabel}>Created By:</span> {detailEntry.created_by_user.name}</div>}
              {detailEntry.cancellation_date && <div><span style={detailLabel}>Cancelled:</span> {detailEntry.cancellation_date}</div>}
              {detailEntry.cancellation_reason && <div style={{ gridColumn: '1 / -1' }}><span style={detailLabel}>Cancel Reason:</span> {detailEntry.cancellation_reason}</div>}
            </div>
            {detailEntry.notes && <div style={{ marginBottom: 16 }}><span style={detailLabel}>Notes:</span><br />{detailEntry.notes}</div>}

            {/* Payment History */}
            {Array.isArray(detailEntry.payment_history) && detailEntry.payment_history.length > 0 && (
              <div>
                <h4 style={{ margin: '0 0 8px', fontSize: '0.92rem', color: '#334155' }}>Payment History</h4>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Date</th>
                      <th style={thStyle}>Previous</th>
                      <th style={thStyle}>New</th>
                      <th style={thStyle}>Difference</th>
                      <th style={thStyle}>User</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailEntry.payment_history.map((h, i) => (
                      <tr key={i}>
                        <td style={tdStyle}>{h.payment_date}</td>
                        <td style={tdStyle}>${Number(h.previous_paid_amount || 0).toFixed(2)}</td>
                        <td style={tdStyle}>${Number(h.new_paid_amount || 0).toFixed(2)}</td>
                        <td style={{ ...tdStyle, color: Number(h.difference) >= 0 ? '#16a34a' : '#ef4444' }}>
                          {Number(h.difference) >= 0 ? '+' : ''}${Number(h.difference || 0).toFixed(2)}
                        </td>
                        <td style={tdStyle}>{h.user ? h.user.name : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const statusBadge = (status) => {
  const colors = { active: '#3b82f6', planned: '#8b5cf6', completed: '#22c55e', cancelled: '#ef4444' };
  return { padding: '2px 8px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 600, color: '#fff', background: colors[status] || '#94a3b8', display: 'inline-block' };
};

const payBadge = (status) => {
  const colors = { unpaid: '#ef4444', partially_paid: '#f59e0b', paid: '#22c55e' };
  return { padding: '2px 8px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 600, color: '#fff', background: colors[status] || '#94a3b8', display: 'inline-block' };
};

const pageStyle = { padding: 28, maxWidth: 1200, margin: '0 auto' };
const tableStyle = { width: '100%', borderCollapse: 'collapse' };
const thStyle = { textAlign: 'left', padding: '8px 10px', fontSize: '0.75rem', color: '#64748b', borderBottom: '2px solid #e2e8f0', fontWeight: 600, textTransform: 'uppercase' };
const tdStyle = { padding: '8px 10px', fontSize: '0.85rem', color: '#334155', borderBottom: '1px solid #f1f5f9' };
const inputStyle = { display: 'block', width: '100%', padding: '7px 10px', borderRadius: 7, border: '1px solid #e2e8f0', fontSize: '0.88rem', marginTop: 4, boxSizing: 'border-box' };
const selectStyle = { padding: '7px 10px', borderRadius: 7, border: '1px solid #e2e8f0', fontSize: '0.88rem', background: '#fff', cursor: 'pointer' };
const primaryBtnStyle = { padding: '8px 18px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 7, fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer' };
const cancelBtnStyle = { padding: '8px 18px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 7, fontSize: '0.88rem', cursor: 'pointer' };
const smallBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', fontSize: '0.92rem' };
const pageBtnStyle = { padding: '6px 14px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', fontSize: '0.82rem', cursor: 'pointer', color: '#475569' };
const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalStyle = { background: '#fff', borderRadius: 12, padding: 28, width: '95%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' };
const formGrid = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' };
const labelStyle = { fontSize: '0.82rem', color: '#475569', fontWeight: 500, display: 'block' };
const detailLabel = { fontWeight: 600, color: '#64748b', fontSize: '0.8rem' };
